import { useEffect, useMemo, useState } from "react";
import styles from "./DataTable.module.css";
import TablePagination from "./TablePagination";

const DataTable = ({
  columns = [],
  data = [],
  showSearch = true,
  wrapCard = true,
  initialPageSize = 10,
  pageSizeOptions = [5, 10, 25, 50, 100],
  showLimiter = true,
  minWidth,
  className = "",
  tableClassName = "",
}) => {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const getValue = (obj, path) =>
    path?.split(".").reduce((o, key) => o?.[key], obj);

  const filteredData = useMemo(() => {
    const query = search.toLowerCase();
    return data.filter((row) =>
      Object.values(row).some((val) =>
        String(val ?? "").toLowerCase().includes(query)
      )
    );
  }, [data, search]);

  const sortedData = useMemo(() => {
    if (!sortKey) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = getValue(a, sortKey);
      const bVal = getValue(b, sortKey);

      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortKey, sortDir]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = sortedData.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    const safeTotal = totalPages || 1;
    setPage((prev) => {
      const next = Math.min(Math.max(1, prev), safeTotal);
      return next === prev ? prev : next;
    });
  }, [totalPages]);

  const handleSort = (key) => {
    if (!key) return;

    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div className={wrapCard ? styles.card : styles.plain}>
      {showSearch ? (
        <div className={styles.searchBar}>
          <input
            type="text"
            placeholder="Search records..."
            className={styles.searchInput}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      ) : null}

      <div className={[styles.desktopTable, className].filter(Boolean).join(" ")}>
        <table
          className={[styles.table, tableClassName].filter(Boolean).join(" ")}
          style={minWidth ? { minWidth } : undefined}
        >
          <thead className={styles.thead}>
            <tr>
              {columns.map((col, i) => (
                <th
                  key={i}
                  onClick={() => col.accessor && handleSort(col.accessor)}
                  className={[
                    styles.th,
                    col.accessor ? styles.thClickable : styles.thDisabled,
                    col.headerClassName,
                  ].filter(Boolean).join(" ")}
                  style={
                    col.width || col.minWidth
                      ? { width: col.width, minWidth: col.minWidth }
                      : undefined
                  }
                >
                  {col.header}
                  {sortKey === col.accessor && (
                    <span className={styles.sortIcon}>
                      {sortDir === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.empty}>
                  No data found
                </td>
              </tr>
            ) : (
              paginatedData.map((row, i) => (
                <tr key={i} className={styles.tr}>
                  {columns.map((col, j) => (
                    <td
                      key={j}
                      className={[styles.td, col.className].filter(Boolean).join(" ")}
                      style={
                        col.width || col.minWidth
                          ? { width: col.width, minWidth: col.minWidth }
                          : undefined
                      }
                    >
                      {col.render
                        ? col.render(row, (page - 1) * pageSize + i)
                        : getValue(row, col.accessor)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.mobileList}>
        {paginatedData.length === 0 ? (
          <div className={styles.empty}>No data found</div>
        ) : (
          paginatedData.map((row, i) => (
            <div key={i} className={styles.mobileCard}>
              {columns.map((col, j) => {
                const isAction = col.header === "Action";
                return (
                  <div
                    key={j}
                    className={[
                      styles.mobileRow,
                      isAction ? styles.mobileRowDivider : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {!isAction && <span className={styles.mobileLabel}>{col.header}</span>}
                    <span className={styles.mobileValue}>
                      {col.render
                        ? col.render(row, (page - 1) * pageSize + i)
                        : getValue(row, col.accessor)}
                    </span>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <TablePagination
        totalItems={sortedData.length}
        page={page}
        limit={pageSize}
        onPageChange={setPage}
        onLimitChange={setPageSize}
        limitOptions={pageSizeOptions}
        showLimiter={showLimiter}
      />
    </div>
  );
};

export default DataTable;
